/**
 * Advanced logger utility for client-side logging
 */

// Define log levels
export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

// Configuration options for the logger
export interface LoggerConfig {
  minLevel: LogLevel;
  enabled: boolean;
  includeTimestamps: boolean;
  includeContext: boolean;
}

// Default configuration
const defaultConfig: LoggerConfig = {
  minLevel: process.env.NODE_ENV === 'production' ? LogLevel.INFO : LogLevel.DEBUG,
  enabled: process.env.NODE_ENV !== 'production' || process.env.NEXT_PUBLIC_ENABLE_CLIENT_LOGS === 'true',
  includeTimestamps: true,
  includeContext: true
};

// Define logger interface
export interface Logger {
  info(message: string, context?: string, ...args: unknown[]): void;
  warn(message: string, context?: string, ...args: unknown[]): void;
  error(message: string | Error, context?: string, ...args: unknown[]): void;
  debug(message: string, context?: string, ...args: unknown[]): void;
  setConfig(config: Partial<LoggerConfig>): void;
}

// Utility functions
const getTimestamp = (): string => {
  return new Date().toISOString();
};

const formatMessage = (level: LogLevel, message: string, context?: string, includeTimestamps = true, includeContext = true): string => {
  const parts: string[] = [];
  
  if (includeTimestamps) {
    parts.push(`[${getTimestamp()}]`);
  }
  
  parts.push(`[${level}]`);
  
  if (includeContext && context) {
    parts.push(`[${context}]`);
  }
  
  parts.push(message);
  return parts.join(' ');
};

// Create the logger instance
class LoggerImpl implements Logger {
  private config: LoggerConfig = { ...defaultConfig };
  
  public setConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }
  
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) {
      return false;
    }
    
    const levels = Object.values(LogLevel);
    const minLevelIndex = levels.indexOf(this.config.minLevel);
    const currentLevelIndex = levels.indexOf(level);
    
    return currentLevelIndex >= minLevelIndex;
  }
  
  public info(message: string, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(
        formatMessage(
          LogLevel.INFO, 
          message, 
          context, 
          this.config.includeTimestamps, 
          this.config.includeContext
        ),
        ...args
      );
    }
  }
  
  public warn(message: string, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(
        formatMessage(
          LogLevel.WARN, 
          message, 
          context, 
          this.config.includeTimestamps, 
          this.config.includeContext
        ),
        ...args
      );
    }
  }
  
  public error(message: string | Error, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorMessage = message instanceof Error ? `${message.message} (${message.name})` : message;
      const stack = message instanceof Error ? message.stack : '';
      
      console.error(
        formatMessage(
          LogLevel.ERROR, 
          errorMessage, 
          context, 
          this.config.includeTimestamps, 
          this.config.includeContext
        ),
        stack,
        ...args
      );
    }
  }
  
  public debug(message: string, context?: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(
        formatMessage(
          LogLevel.DEBUG, 
          message, 
          context, 
          this.config.includeTimestamps, 
          this.config.includeContext
        ),
        ...args
      );
    }
  }
}

// Export singleton instance
export const logger: Logger = new LoggerImpl(); 